/**
 * 本地打包的多语言回退表（中文 / 英文）
 *
 * 远程 `icua-i18n` 不可用、或响应中缺少某 namespace 时，与远程数据合并或单独使用。
 *
 * @module locales/bundled-fallback
 */

import { enFallbackNamespaces } from './en-fallback/namespaces';
import { zhCnFallbackNamespaces } from './zh-cn-fallback/namespaces';

/** 按 i18n 短码（与 {@link getI18nLang} 一致：zh / en）索引的命名空间表 */
const BUNDLED_BY_LANG: Record<string, Record<string, Record<string, unknown>>> = {
  zh: zhCnFallbackNamespaces,
  en: enFallbackNamespaces,
};

/**
 * 深拷贝命名空间资源，避免运行时修改污染静态表
 *
 * @param namespace - 与 `useTranslation('…')` 一致的命名空间名
 * @param table - 语言下的全量命名空间对象
 * @returns 克隆后的键值表；无定义时返回空对象
 */
function cloneNamespaceFromTable(
  namespace: string,
  table: Record<string, Record<string, unknown>>,
): Record<string, unknown> {
  const raw = table[namespace];
  if (!raw || typeof raw !== 'object') return {};
  try {
    if (typeof structuredClone === 'function') {
      return structuredClone(raw) as Record<string, unknown>;
    }
  } catch {
    /* 忽略，走 JSON */
  }
  return JSON.parse(JSON.stringify(raw)) as Record<string, unknown>;
}

/**
 * 获取某语言下某命名空间的本地回退副本
 *
 * @param langShort - `zh` | `en` 等；未知语种时回退到中文表
 * @param namespace - 命名空间
 */
export function getBundledNamespace(langShort: string, namespace: string): Record<string, unknown> {
  const table = BUNDLED_BY_LANG[langShort] ?? BUNDLED_BY_LANG.zh;
  return cloneNamespaceFromTable(namespace, table);
}

/**
 * 供 i18n.init resources 使用：整包浅拷贝引用即可（i18next 会读取）
 */
export function getBundledResourcesForLang(langShort: string): Record<string, Record<string, unknown>> {
  return BUNDLED_BY_LANG[langShort] ?? BUNDLED_BY_LANG.zh;
}
